/**
 * ReportsPage Component
 * 
 * Displays order reports with filtering, search, and 12-month revenue chart.
 * Allows filtering by status, date range, customer name, and phone number.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onBack - Callback to navigate back
 * @param {Function} props.onViewOrder - Callback to view order details
 * @param {Object} props.currentStore - Current store data
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Download, Filter, Home, Eye, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { formatMonDD, getImageUrl } from '../../utils/formatters';

function ReportsPage({ onBack, onViewOrder, currentStore }) {
  // Helper function to get store ID
  const getStoreId = () => {
    return currentStore?.storeId || currentStore?.id;
  };

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  
  // Initialize default dates to current month-to-date
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date();
    
    setStartDateFilter(firstDay.toISOString().split('T')[0]);
    setEndDateFilter(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    fetchOrders();
    fetch12MonthData();
  }, []);

  useEffect(() => {
    // Apply default filters when orders are loaded
    if (orders.length > 0 && startDateFilter && endDateFilter) {
      applyFilters();
    }
  }, [orders, startDateFilter, endDateFilter]);

  const fetch12MonthData = async () => {
    try {
      setChartLoading(true);
      const storeId = getStoreId();
      const response = await axios.get(
        `${API}/reports/12-month-rolling${storeId ? `?store_id=${storeId}` : ''}`
      );
      setChartData(response.data.months || []);
    } catch (error) {
      console.error('Failed to fetch 12-month data:', error);
      toast.error('Failed to load monthly sales chart');
    } finally {
      setChartLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const storeId = getStoreId();
      const response = await axios.get(`${API}/orders${storeId ? `?store_id=${storeId}` : ''}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...orders];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }

    if (phoneSearch) {
      const phoneQuery = phoneSearch.trim();
      filtered = filtered.filter(o => {
        const orderPhone = String(o.customer_info.phone).trim();
        return orderPhone.includes(phoneQuery);
      });
    }

    if (nameSearch) {
      filtered = filtered.filter(o => 
        o.customer_info.name.toLowerCase().includes(nameSearch.toLowerCase())
      );
    }

    if (startDateFilter) {
      filtered = filtered.filter(o => {
        const eventDate = o.customer_info.start_date;
        return eventDate && new Date(eventDate) >= new Date(startDateFilter);
      });
    }

    if (endDateFilter) {
      filtered = filtered.filter(o => {
        const eventDate = o.customer_info.start_date;
        return eventDate && new Date(eventDate) <= new Date(endDateFilter);
      });
    }

    filtered.sort((a, b) => {
      const dateA = a.customer_info.start_date || '';
      const dateB = b.customer_info.start_date || '';
      return dateB.localeCompare(dateA);
    });

    setFilteredOrders(filtered);
    toast.success(`Found ${filtered.length} order(s)`);
  };

  const clearFilters = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date();
    
    setStatusFilter('all');
    setPhoneSearch('');
    setNameSearch('');
    setStartDateFilter(firstDay.toISOString().split('T')[0]);
    setEndDateFilter(today.toISOString().split('T')[0]);
    setFilteredOrders(orders);
    toast.info('Filters reset to current month-to-date');
  };

  const exportToCSV = () => {
    const headers = ['Customer', 'Phone', 'Event Date', 'Status', 'Total', 'Created Date'];
    const rows = filteredOrders.map(order => [
      order.customer_info.name,
      order.customer_info.phone,
      order.customer_info.start_date || 'N/A',
      order.status,
      Math.floor(order.total_amount),
      new Date(order.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orders-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Orders exported to CSV');
  };

  // Calculate KPIs
  const totalOrders = filteredOrders.length;
  const confirmedOrders = filteredOrders.filter(o => o.status === 'confirmed').length;
  const totalConfirmedValue = filteredOrders
    .filter(o => o.status === 'confirmed')
    .reduce((sum, o) => sum + o.total_amount, 0);

  // Custom label for bars
  const CustomBarLabel = (props) => {
    const { x, y, width, value, index } = props;
    const change = chartData[index]?.change_percent || 0;
    const changeColor = change >= 0 ? '#16A34A' : '#DC2626';
    
    return (
      <g>
        <text x={x + width / 2} y={y - 25} fill="#111827" textAnchor="middle" fontSize="13" fontWeight="600">
          ${value.toLocaleString()}
        </text>
        {change !== 0 && (
          <text x={x + width / 2} y={y - 10} fill={changeColor} textAnchor="middle" fontSize="11" fontWeight="500">
            {change > 0 ? '+' : ''}{change}%
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bawarchi-background-muted)' }}>
      {/* Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        <button onClick={onBack} className="btn-bawarchi-secondary" style={{ padding: '8px 20px', minHeight: '40px' }}>
          <Home className="w-4 h-4 mr-2 inline" />
          Back to Dashboard
        </button>
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Monthly sales overview and detailed order reports</p>
        </div>

        {/* 12-Month Rolling Chart - ALWAYS VISIBLE, NOT AFFECTED BY FILTERS */}
        <div className="card-bawarchi section-spacing">
          <h2 className="card-bawarchi-title">Monthly Sales Overview</h2>
          <p className="text-small text-muted" style={{ marginBottom: 'var(--spacing-4)' }}>
            Rolling 12-month confirmed sales (not affected by filters below)
          </p>
          
          {chartLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: 'var(--bawarchi-primary)' }}></div>
              <p className="mt-2 text-muted">Loading chart...</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                <Tooltip 
                  formatter={(value, name) => [`$${value.toLocaleString()}`, 'Sales']}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}
                />
                <Bar dataKey="total_value" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="#EA580C" />
                  ))}
                  <LabelList content={<CustomBarLabel />} position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Filters Card - Below Chart */}
        <div className="card-bawarchi section-spacing">
          <h2 className="card-bawarchi-title">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="label-bawarchi">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="input-bawarchi">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="quote">Quote</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-bawarchi">Phone Number</Label>
              <Input
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                placeholder="Search by phone"
                className="input-bawarchi"
              />
            </div>

            <div>
              <Label className="label-bawarchi">Customer Name</Label>
              <Input
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Search by name"
                className="input-bawarchi"
              />
            </div>

            <div>
              <Label className="label-bawarchi">From Date</Label>
              <Input
                type="date"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                className="input-bawarchi"
              />
            </div>

            <div>
              <Label className="label-bawarchi">To Date</Label>
              <Input
                type="date"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                className="input-bawarchi"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={applyFilters} className="btn-bawarchi-primary">
              <Filter className="w-4 h-4 mr-2 inline" />
              Apply Filters
            </button>
            <button onClick={exportToCSV} className="btn-bawarchi-secondary">
              <Download className="w-4 h-4 mr-2 inline" />
              Export to CSV
            </button>
            <button onClick={clearFilters} className="btn-bawarchi-secondary">
              <X className="w-4 h-4 mr-2 inline" />
              Clear Filters
            </button>
          </div>
        </div>

        {/* KPI Summary Boxes - Single Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 section-spacing">
          <div className="card-bawarchi text-center">
            <p className="text-small text-muted mb-2">Total Orders</p>
            <p className="text-4xl font-bold" style={{ color: 'var(--bawarchi-primary)' }}>
              {totalOrders}
            </p>
          </div>

          <div className="card-bawarchi text-center">
            <p className="text-small text-muted mb-2">Confirmed Orders</p>
            <p className="text-4xl font-bold" style={{ color: 'var(--bawarchi-success)' }}>
              {confirmedOrders}
            </p>
          </div>

          <div className="card-bawarchi text-center">
            <p className="text-small text-muted mb-2">Total Confirmed Value</p>
            <p className="text-4xl font-bold" style={{ color: 'var(--bawarchi-info)' }}>
              ${Math.floor(totalConfirmedValue)}
            </p>
          </div>
        </div>

        {/* Orders Table */}
        <div className="card-bawarchi">
          <h2 className="card-bawarchi-title">Orders ({filteredOrders.length})</h2>
          <div>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: 'var(--bawarchi-primary)' }}></div>
                <p className="mt-2">Loading...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded border border-gray-200">
                <p className="text-gray-600 font-medium">No orders found</p>
                <p className="text-sm text-gray-500 mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="mb-2 text-sm p-2 rounded" style={{ color: 'var(--bawarchi-success)', backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
                  Showing {filteredOrders.length} order(s)
                </div>
                <table className="table-bawarchi">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Event Date</th>
                      <th>Status</th>
                      <th className="text-right">Total</th>
                      <th>Created</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.customer_info.name}</td>
                        <td>{order.customer_info.phone}</td>
                        <td>{order.customer_info.start_date || 'N/A'}</td>
                        <td>
                          <span className={order.status === 'confirmed' ? 'badge-bawarchi badge-success' : 'badge-bawarchi badge-warning'}>
                            {order.status}
                          </span>
                        </td>
                        <td className="text-right font-semibold">${Math.floor(order.total_amount)}</td>
                        <td className="text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                        <td className="text-center">
                          <button
                            onClick={() => onViewOrder(order.id)}
                            className="btn-bawarchi-secondary"
                            style={{ padding: '6px 12px', minHeight: '32px', fontSize: '13px' }}
                            title="View Order"
                          >
                            <Eye className="w-4 h-4 mr-1 inline" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        
        {/* Powered by Caterlog Footer */}
        <div className="text-center py-6 text-sm text-gray-500">
          Powered by <span className="font-semibold text-orange-600">Caterlog</span>
        </div>
      </div>
    </div>
  );
}


// Menu Item Size Selector Component for Cart Review

export default ReportsPage;
